import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { usePOLineItemsSummary } from "@/hooks/usePOLineItems";
import { DollarSign, TrendingUp, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { POExtractionJob } from "@/hooks/usePOExtraction";

interface POFundingSummaryProps {
  purchaseOrderId: string;
  extractionJob?: POExtractionJob | null;
}

export function POFundingSummary({ purchaseOrderId, extractionJob }: POFundingSummaryProps) {
  const { summary, isLoading } = usePOLineItemsSummary(purchaseOrderId);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-20 mb-2" />
              <div className="h-6 bg-muted rounded w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!summary || summary.totalItems === 0) {
    const isExtracting = extractionJob &&
      !['completed', 'failed'].includes(extractionJob.status);

    if (isExtracting) {
      return (
        <Card>
          <CardContent className="p-6 flex items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            Processing funding summary...
          </CardContent>
        </Card>
      );
    }
  }

  const verificationProgress = summary.totalItems > 0
    ? (summary.itemsVerified / summary.totalItems) * 100
    : 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-4">
      {/* Main metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total GEV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalGEV)}</p>
            <p className="text-xs text-muted-foreground">Gross Expected Value</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total NEV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalNEV)}</p>
            <p className="text-xs text-muted-foreground">Net Expected Value</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total NERV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalNERV)}</p>
            <p className="text-xs text-muted-foreground">Net Expected Realizable Value</p>
          </CardContent>
        </Card>

        {/* <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Purchase Price
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalCappedPrice)}</p>
            <p className="text-xs text-muted-foreground">
              {summary.totalCappedPrice < summary.totalSuggestedPrice ? "Capped" : "Suggested"}
            </p>
          </CardContent>
        </Card> */}
      </div>

      {/* Verification progress */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              {summary.itemsNeedingReview > 0 ? (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              ) : (
                <CheckCircle className="h-4 w-4 text-primary" />
              )}
              Verification Status
            </span>
            <span className="text-muted-foreground">
              {summary.itemsVerified} / {summary.totalItems} verified
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={verificationProgress} className="h-2" />
          {summary.itemsNeedingReview > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              {summary.itemsNeedingReview} item{summary.itemsNeedingReview > 1 ? "s" : ""} need review
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
